import React, { useState, useMemo } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import Nav from "@/components/Nav";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Dice5, Percent, Medal, Swords, Plus, X } from "lucide-react";

const TYPES = [
  {
    id: "average",
    icon: <Percent className="w-6 h-6" />,
    title: "Average Share",
    body: "Each round, players earn a share of their table's victory points. Standings = average share across played rounds.",
  },
  {
    id: "placement",
    icon: <Medal className="w-6 h-6" />,
    title: "Fixed Placement Points",
    body: "You set the points for 1st/2nd/3rd/... at each table. Ties split points evenly. Sum across rounds = standings.",
  },
  {
    id: "bracket",
    icon: <Swords className="w-6 h-6" />,
    title: "Single Elimination",
    body: "1v1 bracket. Winner of each match advances. BYEs auto-advance. Random or manual seeding.",
  },
];

export default function CreateTournament() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [form, setForm] = useState({
    tournament_type: "average",
    name: "",
    description: "",
    game_name: "",
    total_players: 8,
    players_per_table: 4,
    num_rounds: 3,
    championship_players: 4,
    timer_minutes: 60,
    placement_points: [5, 3, 1],
    bracket_seeding: "random",
  });
  const [submitting, setSubmitting] = useState(false);

  const bracketRounds = useMemo(() => {
    if (form.tournament_type !== "bracket") return null;
    return Math.max(1, Math.ceil(Math.log2(Math.max(2, +form.total_players || 2))));
  }, [form.tournament_type, form.total_players]);

  if (user && !(user.role === "super_admin" || (user.role === "organizer" && user.organizer_status === "approved"))) {
    return <Navigate to="/dashboard" replace />;
  }

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.tournament_type !== "bracket" && form.championship_players > form.total_players) {
      toast.error("Championship players can't exceed total players");
      return;
    }
    if (form.tournament_type === "placement") {
      const clean = form.placement_points.filter(p => p !== "" && !Number.isNaN(+p));
      if (clean.length === 0) {
        toast.error("Add at least one placement point value (e.g. 5 for 1st place)");
        return;
      }
    }
    setSubmitting(true);
    try {
      const payload = {
        tournament_type: form.tournament_type,
        name: form.name,
        description: form.description,
        game_name: form.game_name,
        total_players: +form.total_players,
        players_per_table: form.tournament_type === "bracket" ? 2 : +form.players_per_table,
        num_rounds: form.tournament_type === "bracket" ? bracketRounds : +form.num_rounds,
        championship_players: form.tournament_type === "bracket" ? 2 : +form.championship_players,
        timer_minutes: +form.timer_minutes,
      };
      if (form.tournament_type === "placement") {
        payload.placement_points = form.placement_points.map(p => +p).filter(p => !Number.isNaN(p));
      }
      if (form.tournament_type === "bracket") {
        payload.bracket_seeding = form.bracket_seeding;
      }
      const r = await api.post("/tournaments", payload);
      toast.success("Tournament created!");
      navigate(`/tournament/${r.data.tournament_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create");
    } finally {
      setSubmitting(false);
    }
  };

  const tables = Math.floor(form.total_players / form.players_per_table);
  const byes = form.total_players - tables * form.players_per_table;

  return (
    <div className="min-h-screen bg-slate-950">
      <Nav />
      <div className="max-w-5xl mx-auto px-6 lg:px-10 py-10">
        <div className="text-xs text-slate-500 font-mono-data tracking-widest">NEW TOURNAMENT</div>
        <h1 className="text-4xl md:text-5xl font-bold mt-2 text-slate-50">Configure the run.</h1>
        <p className="text-slate-400 mt-2">Pick a scoring style, then dial in the rules.</p>

        <form onSubmit={submit} className="mt-10">
          {/* Type picker */}
          <div className="text-xs text-slate-500 font-mono-data tracking-widest mb-3">SCORING TYPE</div>
          <div className="grid md:grid-cols-3 gap-3 mb-8">
            {TYPES.map(tp => (
              <button
                key={tp.id}
                type="button"
                onClick={() => set("tournament_type", tp.id)}
                data-testid={`type-${tp.id}`}
                className={`text-left p-5 rounded-xl border transition-all duration-200 ${form.tournament_type === tp.id ? 'border-amber-500 bg-amber-500/10 -translate-y-0.5' : 'border-white/10 bg-slate-900/40 hover:border-white/20'}`}
              >
                <div className={`w-10 h-10 rounded-md flex items-center justify-center ${form.tournament_type === tp.id ? 'bg-amber-500 text-slate-950' : 'bg-slate-800 text-slate-300'}`}>{tp.icon}</div>
                <div className="mt-4 font-semibold text-slate-100">{tp.title}</div>
                <div className="text-xs text-slate-500 mt-1 leading-relaxed">{tp.body}</div>
              </button>
            ))}
          </div>

          <div className="grid lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              <Card>
                <Section label="Basics" />
                <div className="space-y-4 mt-4">
                  <div>
                    <Label className="text-slate-300">Tournament name</Label>
                    <Input required value={form.name} onChange={(e) => set("name", e.target.value)} className="bg-slate-950 border-white/10 mt-1.5" placeholder="Winter Wingspan Open" data-testid="create-name" />
                  </div>
                  <div>
                    <Label className="text-slate-300">Game / edition</Label>
                    <Input value={form.game_name} onChange={(e) => set("game_name", e.target.value)} className="bg-slate-950 border-white/10 mt-1.5" placeholder="Wingspan · European Expansion" data-testid="create-game" />
                  </div>
                  <div>
                    <Label className="text-slate-300">Description</Label>
                    <Textarea value={form.description} onChange={(e) => set("description", e.target.value)} className="bg-slate-950 border-white/10 mt-1.5" placeholder="Optional notes for participants…" data-testid="create-desc" />
                  </div>
                </div>
              </Card>

              <Card>
                <Section label="Structure" />
                <div className="grid grid-cols-2 gap-4 mt-4">
                  <NumField label="Total players" value={form.total_players} onChange={(v) => set("total_players", v)} min={2} testId="create-total-players" />
                  {form.tournament_type !== "bracket" && (
                    <>
                      <NumField label="Players / table" value={form.players_per_table} onChange={(v) => set("players_per_table", v)} min={2} testId="create-per-table" />
                      <NumField label="# of rounds" value={form.num_rounds} onChange={(v) => set("num_rounds", v)} min={1} testId="create-rounds" />
                      <NumField label="Championship seats" value={form.championship_players} onChange={(v) => set("championship_players", v)} min={2} testId="create-champ" />
                    </>
                  )}
                  <NumField label="Timer (minutes)" value={form.timer_minutes} onChange={(v) => set("timer_minutes", v)} min={1} testId="create-timer" />
                </div>
              </Card>

              {/* Placement points editor */}
              {form.tournament_type === "placement" && (
                <Card>
                  <Section label="Point system" />
                  <p className="text-sm text-slate-400 mt-2">
                    Set the points a player earns for finishing 1st, 2nd, 3rd, etc. at their table. Ties split evenly.
                  </p>
                  <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {form.placement_points.map((v, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <div className="text-slate-500 font-mono-data text-xs w-14">{ordinal(i + 1)}</div>
                        <Input
                          type="number"
                          value={v}
                          onChange={(e) => {
                            const arr = [...form.placement_points];
                            arr[i] = e.target.value;
                            set("placement_points", arr);
                          }}
                          className="bg-slate-950 border-white/10 font-mono-data"
                          data-testid={`placement-pts-${i + 1}`}
                        />
                        {form.placement_points.length > 1 && (
                          <button type="button" onClick={() => {
                            const arr = form.placement_points.filter((_, j) => j !== i);
                            set("placement_points", arr);
                          }} className="text-slate-500 hover:text-rose-400" data-testid={`placement-remove-${i+1}`}>
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => set("placement_points", [...form.placement_points, 0])}
                      className="inline-flex items-center gap-1 text-xs text-amber-400 hover:text-amber-300 font-mono-data tracking-widest uppercase"
                      data-testid="placement-add-slot"
                    >
                      <Plus className="w-3 h-3" /> add position
                    </button>
                  </div>
                  <p className="text-xs text-slate-500 mt-4">Any players who finish beyond the last position get 0 points for that round.</p>
                </Card>
              )}

              {/* Bracket seeding */}
              {form.tournament_type === "bracket" && (
                <Card>
                  <Section label="Bracket seeding" />
                  <p className="text-sm text-slate-400 mt-2">Random seeding is the default — great for casual events. Manual seeding lets you place your top players so they don't meet until the finals.</p>
                  <div className="mt-4 grid grid-cols-2 gap-3">
                    <SeedCard active={form.bracket_seeding === "random"} onClick={() => set("bracket_seeding", "random")} title="Random" desc="Shuffle at Round 1 setup" testId="seed-random" />
                    <SeedCard active={form.bracket_seeding === "manual"} onClick={() => set("bracket_seeding", "manual")} title="Manual" desc="You'll order players before the bracket starts" testId="seed-manual" />
                  </div>
                </Card>
              )}
            </div>

            <div className="space-y-6">
              <Card>
                <Section label="Round preview" />
                <div className="mt-4 space-y-3 font-mono-data">
                  {form.tournament_type === "bracket" ? (
                    <>
                      <Row k="Matches R1" v={Math.floor((+form.total_players || 0) / 2)} />
                      <Row k="R1 BYEs" v={(+form.total_players || 0) % 2} highlight={((+form.total_players || 0) % 2) === 1} />
                      <Row k="Bracket rounds" v={bracketRounds} />
                      <Row k="Final match" v="Championship" />
                    </>
                  ) : (
                    <>
                      <Row k="Tables" v={tables} />
                      <Row k="Seats used" v={tables * form.players_per_table} />
                      <Row k="BYE players" v={byes} highlight={byes > 0} />
                      <Row k="Rounds + championship" v={`${form.num_rounds} + 1`} />
                    </>
                  )}
                </div>
              </Card>
              <Card>
                <div className="flex items-start gap-3">
                  <Dice5 className="w-5 h-5 text-amber-400 mt-1" />
                  <div className="text-sm text-slate-400">
                    {form.tournament_type === "bracket"
                      ? "Winners of each 1v1 advance. Odd counts → last seed gets a BYE and auto-advances."
                      : "Each round, the app randomizes seating. If the count doesn't divide evenly, extra players get a BYE."}
                  </div>
                </div>
              </Card>
              <Button type="submit" disabled={submitting} className="w-full h-12 bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold" data-testid="create-submit">
                {submitting ? "Creating…" : "Create Tournament"}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

function ordinal(n) {
  const s = ["th","st","nd","rd"], v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

const Card = ({ children }) => (
  <div className="rounded-2xl border border-white/10 bg-slate-900/40 p-6">{children}</div>
);
const Section = ({ label }) => (
  <div className="text-xs text-slate-500 font-mono-data tracking-widest">{label.toUpperCase()}</div>
);
const NumField = ({ label, value, onChange, min, testId }) => (
  <div>
    <Label className="text-slate-300">{label}</Label>
    <Input type="number" min={min} value={value} onChange={(e) => onChange(e.target.value === "" ? "" : +e.target.value)} className="bg-slate-950 border-white/10 mt-1.5 font-mono-data" data-testid={testId} />
  </div>
);
const Row = ({ k, v, highlight }) => (
  <div className="flex items-center justify-between text-sm">
    <span className="text-slate-500 uppercase tracking-wider">{k}</span>
    <span className={highlight ? "text-rose-400" : "text-slate-100"}>{v}</span>
  </div>
);
function SeedCard({ active, onClick, title, desc, testId }) {
  return (
    <button type="button" onClick={onClick} data-testid={testId}
      className={`text-left p-4 rounded-xl border transition-all duration-200 ${active ? 'border-amber-500 bg-amber-500/10' : 'border-white/10 bg-slate-950 hover:border-white/20'}`}>
      <div className="font-semibold text-slate-100">{title}</div>
      <div className="text-xs text-slate-500 mt-1">{desc}</div>
    </button>
  );
}
